import { useState } from 'react';

import type { Availability, AvailabilityFlight, AvailabilityLeg, ClassAvail } from '../../lib/types';

/** The host wraps a class ladder after this many codes, then starts a new line. */
const CLASSES_PER_LINE = 18;

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * The ladder prints in one colour. Neither seats nor cabin change it: the host
 * writes every code as the same kind of span, and a line closed end to end looks
 * exactly like a wide open one. What an agent reads is the digit, not a tone.
 */
function ClassLadder({ classes }: { classes: ClassAvail[] }) {
  return (
    <div className="avail__classes">
      {chunk(classes, CLASSES_PER_LINE).map((row, index) => (
        <div className="avail__class-row" key={index}>
          {row.map((c) => (
            <span key={c.code} className="avail__class">
              {c.code}
              {c.seats}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * The day change as the drawer writes it — "(+1)" in parentheses, where the
 * flight line prints a bare "+1". Without it the drawer reads as arriving the
 * morning it left, which is the one thing an agent must not misread.
 */
function dayChangeSuffix(days: number): string {
  if (days === 0) return '';
  return ` (${days > 0 ? '+' : ''}${days})`;
}

/** The bare "+1" the flight line prints, and nothing at all for the same day. */
function dayChangeMark(days: number): string {
  if (days === 0) return '';
  return days > 0 ? `+${days}` : `${days}`;
}

/** One `Label: value` pair on the detail line. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <span className="avail__fact">
      <span className="avail__fact-label">{label}:</span> {value}
    </span>
  );
}

const openClasses = (leg: AvailabilityLeg) => leg.classes.filter((c) => c.seats > 0);

const seatsFor = (leg: AvailabilityLeg, code: string) =>
  leg.classes.find((c) => c.code === code)?.seats ?? 0;

/**
 * Why the Sell button is disabled. It names the flight, because on a
 * connection "only 2 seats open in Q" leaves the agent guessing which of the
 * two class boxes to change.
 */
function refusal(leg: AvailabilityLeg, code: string): string {
  const flight = `${leg.carrier}${leg.number}`;
  if (code === '') return `No class is open on ${flight}`;
  return `Only ${seatsFor(leg, code)} seats open in ${code} on ${flight}`;
}

interface FlightRowProps {
  flight: AvailabilityFlight;
  /** Owned by the display: at most one line is open at a time. */
  expanded: boolean;
  onToggle: () => void;
  onSell: (entry: string) => void;
}

function FlightRow({ flight, expanded, onToggle, onSell }: FlightRowProps) {
  // The columns print the leg that leaves the searched origin. A connection is
  // still one line: the onward leg is what the second class box sells, not a
  // second row, which would read as an unrelated flight to the hub.
  const lead = flight.legs[0];
  const last = flight.legs[flight.legs.length - 1];

  const [passengers, setPassengers] = useState('1');
  const [sellClasses, setSellClasses] = useState(() =>
    flight.legs.map((leg) => openClasses(leg)[0]?.code ?? ''),
  );
  const setLegClass = (index: number, code: string) =>
    setSellClasses((current) => current.map((c, i) => (i === index ? code : c)));

  const label =
    flight.legs.length > 1
      ? `${lead.carrier} ${lead.number} ${lead.origin} to ${last.destination} via ${lead.destination}`
      : `${lead.carrier} ${lead.number} ${lead.origin} to ${lead.destination}`;

  // The journey is one sell, so a segment with nothing open in the class it was
  // given stops the whole line rather than selling the half that would go.
  const shortIndex = flight.legs.findIndex(
    (leg, i) => sellClasses[i] === '' || Number(passengers) > seatsFor(leg, sellClasses[i]),
  );
  const short = shortIndex === -1 ? null : flight.legs[shortIndex];

  return (
    <div className={`avail__row-group${expanded ? ' avail__row-group--open' : ''}`}>
      <div className="avail__row">
        <span className="avail__line">{flight.line}</span>
        <span className="avail__carrier">{lead.carrier}</span>
        <span className="avail__number">{lead.number}</span>
        <ClassLadder classes={lead.classes} />
        {/* A continuation leaves where the line above it landed, so the host
            leaves the origin blank rather than repeating the port. */}
        <span className="avail__port">{flight.continues ? '' : lead.origin}</span>
        <span className="avail__port">{lead.destination}</span>
        {/* Before the clock, where the host puts it: 01:55 on the line under a
            23:30 arrival is the next morning, and the clock cannot say so. */}
        <span className="avail__daychange">{dayChangeMark(lead.departDayOffset)}</span>
        <span className="avail__time">{lead.depart}</span>
        <span className="avail__time">{lead.arrive}</span>
        {/* Its own column, not a superscript on the clock: the host keeps a slot
            for it on every line so the equipment codes stay in column. */}
        <span className="avail__daychange">{dayChangeMark(lead.dayChange)}</span>
        <span className="avail__equip">{lead.equipment}</span>
        <span className="avail__stops">{lead.stops}</span>
        <span className="avail__duration">{lead.duration}</span>
        <span aria-hidden="true" />
        <button
          type="button"
          className={`avail__expand${expanded ? ' avail__expand--open' : ''}`}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Hide' : 'Show'} details for ${label}`}
          onClick={onToggle}
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <path d="M3 6l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.6" />
          </svg>
        </button>
      </div>

      {expanded && (
        <>
          <div className="avail__detail">
            <Fact label="From" value={`${lead.origin} ${lead.departDate} at ${lead.depart}`} />
            <Fact
              label="To"
              value={`${lead.destination} ${lead.arriveDate} at ${lead.arrive}${dayChangeSuffix(lead.dayChange)}`}
            />
            {/* The host writes the line in caps but the detail in sentence case. */}
            <Fact label="Flight Time" value={lead.duration} />
            <Fact label="Meals" value={lead.meals.length ? lead.meals.join(', ') : 'No meal service'} />
            <Fact label="Equipment" value={lead.equipName} />
            <Fact label="Operates" value={lead.operates.join(', ')} />
            <Fact label="On-Time" value={lead.onTime} />
            <Fact label="Air Miles" value={lead.airMiles ? lead.airMiles.toLocaleString() : 'No data'} />
            <Fact label="Time Diff" value={lead.timeDiff} />
            <Fact label="Connectivity" value={lead.connectivity} />
            <Fact label="E-ticket" value={lead.eTicket ? 'Yes' : 'No'} />
          </div>

          <div className="avail__sell">
            <label className="avail__sell-field">
              <span>Passengers</span>
              <select value={passengers} onChange={(event) => setPassengers(event.target.value)}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>

            {/* One class box per segment, stacked, so the Sell button sits on
                the bottom line beside the last segment it books. A connection
                goes to the host as one married sell, a class and line number
                per leg (01K1R2): each onward leg is printed on the line below. */}
            <div className="avail__sell-classes">
              {flight.legs.map((leg, index) => (
                <label className="avail__sell-field" key={`${leg.carrier}${leg.number}-${leg.origin}`}>
                  <span>
                    Class {leg.origin}-{leg.destination}
                  </span>
                  <select
                    value={sellClasses[index]}
                    onChange={(event) => setLegClass(index, event.target.value)}
                  >
                    {openClasses(leg).map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>

            <button
              type="button"
              className="avail__sell-button"
              disabled={short !== null}
              title={short === null ? undefined : refusal(short, sellClasses[shortIndex])}
              onClick={() =>
                onSell(`0${passengers}${sellClasses.map((c, i) => `${c}${flight.line + i}`).join('')}`)
              }
            >
              Sell
            </button>
          </div>
        </>
      )}
    </div>
  );
}

interface AvailabilityDisplayProps {
  availability: Availability;
  /** Sends the host sell entry the Sell button builds. */
  onSell: (entry: string) => void;
}

/** The host availability display: a header line and the flights beneath it. */
export function AvailabilityDisplay({ availability, onSell }: AvailabilityDisplayProps) {
  // One open line per display, held here rather than in each row: opening a
  // second drawer pushes the first one off the screen, and an agent comparing
  // two flights ends up scrolling between two half-visible panels instead of
  // reading either. The line number identifies it, since it is what the sell
  // entry quotes.
  const [openLine, setOpenLine] = useState<number | null>(null);

  return (
    <div className="avail">
      <div className="avail__header">{availability.header}</div>
      {availability.flights.map((flight) => (
        <FlightRow
          key={flight.line}
          flight={flight}
          expanded={openLine === flight.line}
          onToggle={() => setOpenLine((open) => (open === flight.line ? null : flight.line))}
          onSell={onSell}
        />
      ))}
      {availability.truncated && (
        <div className="avail__footer">DISPLAY TRUNCATED - ADD A TIME OR AIRLINE QUALIFIER TO NARROW</div>
      )}
    </div>
  );
}
